import { observer } from "mobx-react-lite";
import MainLayout, { MAIN_RIGHT_TOP_HEIGHT } from "../MainLayout";
import { useLocation, useNavigate } from "react-router-dom";
import { VerticalLayout } from "../../common/common-layout";
import { TitleLargeMedium, TitleMediumBold, TitleNormal } from "../../common/common-text";
import { useEffect, useState } from "react";
import BTBox from "../../common/components/BTBox";
import { BTSelectBox } from "../../common/components/BTSelectBox";
import { BTDateComponent } from "../../common/components/BTDateComponent";
import { BTCheckBox } from "../../common/components/BTCheckBox";
import { dialogStore } from "../../store/DialogStore";
import { requestDeleteAllFiles, requestDeletePatentDocuments } from "../../service/BoardService";
import TextField from "@mui/material/TextField";
import BTButton from "../../common/components/BTButton";
import theme from "../../common/theme";
import Divider from "@mui/material/Divider";
import { formatDateToYMD } from "../../common/utils/DateUtils";
import Dialog from "@mui/material/Dialog";

const PatentBoardDetail = () => {
    return (
        <MainLayout>
            <PatentBoardDetailBody />
        </MainLayout>
    );
};

export default PatentBoardDetail;

const PatentBoardDetailBody = observer(() => {
    const location = useLocation();
    const navigate = useNavigate();
    const [patent, setPatent] = useState<any>(null);
    const [patentTypeOptions, setPatentTypeOptions] = useState<{ label: string; value: string }[]>([]);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewSrc, setPreviewSrc] = useState<string | null>(null);

    useEffect(() => {
        const loadPatent = async () => {
            const statePatent = location.state as any;
            if (statePatent?.patent) {
                setPatent(statePatent.patent);
            }
            if (statePatent?.patentTypeOptions) {
                setPatentTypeOptions(statePatent.patentTypeOptions);
            }
        };
        loadPatent();
    }, [location.state]);

    const handleEdit = () => {
        if (!patent) return;
        navigate("/board/patent/edit", {
            state: {
                patent,
                patentTypeOptions,
            },
        });
    };

    const handleDelete = async () => {
        if (!patent) return;

        const deleteConfirmed = await dialogStore.showAsyncDialog({
            title: "경고",
            message: "현재 특허건을 정말로 삭제하시겠습니까?",
            isCaution: true,
        });

        if (!deleteConfirmed) return;

        try {
            await requestDeletePatentDocuments([patent.id]);
            await requestDeleteAllFiles("patent", patent.id);

            dialogStore.showDialog({
                title: "삭제 완료",
                message: "특허건이 삭제되었습니다.",
                onConfirm: handleBack,
                onClose: handleBack,
            });
        } catch (e) {
            console.error("특허 삭제 실패", e);
            dialogStore.showDialog({
                title: "오류",
                message: "특허 삭제 중 오류가 발생했습니다.",
                onConfirm: () => {},
                onClose: () => {},
            });
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    if (!patent) return null;

    return (
        <VerticalLayout height={"auto"} paddingLeft={36} paddingRight={36}>
            {/* 상단 페이지 타이틀 */}
            <TitleLargeMedium>특허 정보 상세</TitleLargeMedium>
            <BTBox height="24px" />
            <Divider style={{ width: "100%", backgroundColor: '#D9D9D9' }} />

            <VerticalLayout height={"auto"} paddingBottom={36}>
                {/* 섹션: 특허 정보 (홍보 상세 UI 패턴 준수) */}
                <TitleMediumBold style={{ marginTop: "20px" }}>특허 정보</TitleMediumBold>
                <BTBox height="16px" />

                {/* 1행: 인증(특허) 유형 - SelectBox만 표시 */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                        flexWrap: "nowrap",
                        fontSize: "14px",
                        color: "#6C757D",
                    }}
                >
                    <b style={{ whiteSpace: "nowrap", width: '80px' }}>인증 유형</b>
                    <div style={{ width: 260, flex: "0 0 auto" }}>
                        <BTSelectBox
                            value={patent.patentType}
                            readOnly
                            options={patentTypeOptions}
                            onChange={() => {}}
                        />
                    </div>
                </div>

                <BTBox height="16px" />

                {/* 2행: 특허명 | 특허 취득 일자 | 글 공개 여부 (Divider 없이 단순 가로정렬) */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                        flexWrap: "wrap",
                        fontSize: "14px",
                        color: "#6C757D",
                    }}
                >
                    {/* 특허명 */}
                    <b style={{ whiteSpace: "nowrap", width: '80px' }}>특허명</b>
                    <div>{patent.title}</div>

                    <Divider
                        orientation="vertical"
                        sx={{ borderColor: "#6C757D", mx: "4px" }}
                    />
                    {/* 특허 취득 일자 */}
                    <b style={{ whiteSpace: "nowrap" }}>특허 취득 일자</b>
                    <div>{patent.acquisitionDate ? formatDateToYMD(patent.acquisitionDate) : '-'}</div>

                    <Divider
                        orientation="vertical"
                        sx={{ borderColor: "#6C757D", mx: "4px" }}
                    />

                    {/* 글 공개 여부 */}
                    <b style={{ whiteSpace: "nowrap" }}>글 공개 여부</b>
                    <div>{patent.isPublished ? "✅" : "❌"}</div>
                </div>

                <BTBox height="16px" />

                {/* 2.5행: 영문/중문 특허명 */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                        flexWrap: "wrap",
                        fontSize: "14px",
                        color: "#6C757D",
                    }}
                >
                    {/* 영문 특허명 */}
                    <b style={{ whiteSpace: "nowrap", width: '80px' }}>영문 특허명</b>
                    <div>{patent.englishTitle || '-'}</div>

                    <Divider orientation="vertical" sx={{ borderColor: "#6C757D", mx: "4px" }} />

                    {/* 중문 특허명 */}
                    <b style={{ whiteSpace: "nowrap" }}>중문 특허명</b>
                    <div>{patent.chineseTitle || '-'}</div>
                </div>

                <BTBox height="16px" />
                <Divider style={{ width: "100%", backgroundColor: '#D9D9D9' }} />

                {/* 섹션: 특허 이미지 (BTReader 사용 안 함) */}
                <TitleMediumBold style={{ marginTop: "20px" }}>특허 이미지</TitleMediumBold>
                <BTBox height="16px" />

                {/* 대표 이미지 - 기존 UI 유지 */}
                <div
                    style={{
                        width: "200px",
                        height: "300px",
                        border: "1px solid #ccc",
                        borderRadius: "6px",
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#fafafa",
                        cursor: "zoom-in",
                    }}
                    onClick={() => {
                        setPreviewSrc(patent.representImage);
                        setPreviewOpen(true);
                    }}
                >
                    <img
                        src={patent.representImage}
                        alt="썸네일"
                        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "cover" }}
                    />
                </div>

                {/* 이미지 미리보기 다이얼로그 (흰색 불투명 배경, 이미지보다 약간 크게) */}
                <Dialog
                    open={previewOpen}
                    onClose={() => setPreviewOpen(false)}
                    PaperProps={{
                        sx: {
                            backgroundColor: '#fff',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                            borderRadius: '8px',
                            p: 2,
                        },
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {previewSrc && (
                            <img
                                src={previewSrc}
                                alt="미리보기"
                                style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain' }}
                            />
                        )}
                    </div>
                </Dialog>

                <BTBox height="36px" />
                {/* 하단 액션 버튼들 */}
                <div
                    style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <BTButton color="#FC5A5A" textColor="white" onClick={handleDelete}>
                        삭제
                    </BTButton>
                    <div style={{ display: "flex", gap: "24px" }}>
                        <BTButton color={theme.colors.bbTech} textColor="white" onClick={handleEdit}>
                            수정
                        </BTButton>
                        <BTButton onClick={handleBack}>목록</BTButton>
                    </div>
                </div>
            </VerticalLayout>
        </VerticalLayout>
    );
});